import React, { useState, useEffect } from "react";
import axios from "axios";
import { Download, Filter, X, User, Calendar, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getStatus(timeStr, dateStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m;
  const day = new Date(dateStr).getDay();
  if (day === 0 || day === 6) {
    if (total <= 14 * 60 + 9) return "on_time";
    if (total <= 14 * 60 + 49) return "late";
    return "absent";
  } else {
    if (total <= 15 * 60 + 33) return "on_time";
    if (total <= 16 * 60 + 9) return "late";
    return "absent";
  }
}

export default function Reports() {
  const [attendance, setAttendance] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");
  const [workerFilter, setWorkerFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerHistory, setWorkerHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [cultDays, setCultDays] = useState([2, 5]);
  const [showCultEditor, setShowCultEditor] = useState(false);
  const [savingCult, setSavingCult] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [a, w, d] = await Promise.all([
        axios.get(`${API}/attendance`),
        axios.get(`${API}/workers`),
        axios.get(`${API}/departments`)
      ]);
      setAttendance(a.data);
      setWorkers(w.data);
      setDepartments(d.data);
      setLoading(false);
    } catch (e) {
      toast.error("Erreur lors du chargement");
      setLoading(false);
    }
  };

  const fetchWorkerHistory = async (workerId, workerName) => {
    setLoadingHistory(true);
    setSelectedWorker(workerName);
    setCurrentWorkerId(workerId);
    try {
      const res = await axios.get(`${API}/workers/${workerId}/history`);
      setWorkerHistory(res.data);
      if (res.data.cult_weekdays) setCultDays(res.data.cult_weekdays);
    } catch (e) {
      toast.error("Erreur lors du chargement de l'historique");
    }
    setLoadingHistory(false);
  };

  const DAY_NAMES = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
  const DAY_SHORT = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

  const toggleCultDay = (d) => {
    setCultDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  };

  const [currentWorkerId, setCurrentWorkerId] = useState(null);

  const saveCultDays = async () => {
    setSavingCult(true);
    try {
      await axios.post(`${API}/cult-days`, { weekdays: cultDays });
      toast.success("Jours de culte mis a jour !");
      setShowCultEditor(false);
      if (currentWorkerId) {
        const res = await axios.get(`${API}/workers/${currentWorkerId}/history`);
        setWorkerHistory(res.data);
        if (res.data.cult_weekdays) setCultDays(res.data.cult_weekdays);
      }
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    }
    setSavingCult(false);
  };

  const filteredAttendance = attendance.filter(record => {
    if (dateFilter && record.date !== dateFilter) return false;
    if (workerFilter !== "all" && record.worker_id !== workerFilter) return false;
    if (deptFilter !== "all") {
      const worker = workers.find(w => w.id === record.worker_id);
      if (!worker || worker.department_id !== deptFilter) return false;
    }
    return true;
  });

  const onTimeCount = filteredAttendance.filter(r => getStatus(r.time, r.date) === "on_time").length;
  const lateCount = filteredAttendance.filter(r => getStatus(r.time, r.date) === "late").length;
  const absentScanned = filteredAttendance.filter(r => getStatus(r.time, r.date) === "absent").length;
  const totalWorkers = workers.length;
  const presentCount = onTimeCount + lateCount;
  const absentGeneral = Math.max(0, totalWorkers - filteredAttendance.length);
  const absentCount = absentScanned + absentGeneral;
  const total = filteredAttendance.length || 1;
  const totalAll = totalWorkers || 1;
  const onTimePct = Math.round(onTimeCount / totalAll * 100);
  const latePct = Math.round(lateCount / totalAll * 100);
  const absentScannedPct = Math.round(absentScanned / totalAll * 100);
  const absentGeneralPct = Math.round(absentGeneral / totalAll * 100);
  const absentPct = Math.round(absentCount / totalAll * 100);

  const pieData = [
    { name: "A l'heure", value: onTimeCount, color: "#f97316" },
    { name: "En retard", value: lateCount, color: "#f59e0b" },
    { name: "Absent", value: absentCount, color: "#ef4444" },
  ].filter(d => d.value > 0);

  const getRows = () => filteredAttendance.map(record => {
    const status = getStatus(record.time, record.date);
    const label = status === "on_time" ? "A l'heure" : status === "late" ? "Retard" : "Absent";
    const color = status === "on_time" ? "#f97316" : status === "late" ? "#f59e0b" : "#ef4444";
    return { record, status, label, color };
  });

  const exportToPDF = () => {
    if (filteredAttendance.length === 0) { toast.error("Aucune donnee a exporter"); return; }
    const rows = getRows().map(({record, label, color}) =>
      `<tr><td>${new Date(record.date).toLocaleDateString("fr-FR")}</td><td>${record.time.substring(0,5)}</td><td>${record.matricule}</td><td>${record.worker_name}</td><td>${record.department_name || "N/A"}</td><td><span style="background:${color}20;color:${color};padding:2px 8px;border-radius:4px;font-weight:600">${label}</span></td></tr>`
    ).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Rapport Scanner</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;color:#1e293b;padding:30px}
.header{text-align:center;margin-bottom:25px;border-bottom:3px solid #f97316;padding-bottom:15px}
.header h1{font-size:24px;color:#f97316}
.stats{display:flex;gap:15px;margin-bottom:15px}
.stat{flex:1;padding:15px;border-radius:10px;text-align:center}
.stat.g{background:#fff7ed;border:2px solid #f97316}
.stat.o{background:#fef3c7;border:2px solid #f59e0b}
.stat.r{background:#fee2e2;border:2px solid #ef4444}
.stat .n{font-size:30px;font-weight:bold}
.stat.g .n{color:#f97316}.stat.o .n{color:#f59e0b}.stat.r .n{color:#ef4444}
.absent-detail{display:flex;gap:15px;margin-bottom:15px}
.abs-box{flex:1;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center}
.abs-box.scanned{background:#fee2e2;border-left:4px solid #ef4444}
.abs-box.general{background:#f1f5f9;border-left:4px solid #94a3b8}
.bar-row{display:flex;align-items:center;margin-bottom:10px}
.bar-lbl{width:130px;font-size:12px;font-weight:600}
.bar-bg{flex:1;background:#f1f5f9;border-radius:6px;height:22px;overflow:hidden}
.bar-fill{height:100%;border-radius:6px;display:flex;align-items:center;padding-left:8px;color:white;font-size:12px;font-weight:bold}
.bars{margin-bottom:20px}
table{width:100%;border-collapse:collapse;font-size:12px}
th{background:#f97316;color:white;padding:9px 10px;text-align:left}
td{padding:8px 10px;border-bottom:1px solid #e2e8f0}
tr:nth-child(even){background:#fff7ed}
.footer{margin-top:20px;text-align:center;color:#94a3b8;font-size:11px}
</style></head><body>
<div class="header"><h1>ScannerBenj Pro</h1>
<p>Rapport de Pointage - ${new Date().toLocaleDateString("fr-FR")} ${new Date().toLocaleTimeString("fr-FR")}</p>
<p style="color:#f97316;font-weight:600;margin-top:4px">${filteredAttendance.length} enregistrements | ${totalWorkers} ouvriers</p></div>
<div class="stats">
<div class="stat g"><div class="n">${onTimeCount}</div><div>A l'heure</div><div style="font-size:18px;font-weight:bold;color:#f97316">${onTimePct}%</div></div>
<div class="stat o"><div class="n">${lateCount}</div><div>En retard</div><div style="font-size:18px;font-weight:bold;color:#f59e0b">${latePct}%</div></div>
<div class="stat r"><div class="n">${absentCount}</div><div>Absents Total</div><div style="font-size:18px;font-weight:bold;color:#ef4444">${absentPct}%</div></div>
</div>
<div class="absent-detail">
<div class="abs-box scanned"><div><div style="font-weight:bold;color:#ef4444">Absents Scannes (trop tard)</div><div style="font-size:11px;color:#64748b">Ont scanne apres la limite</div></div><div style="font-size:28px;font-weight:bold;color:#ef4444">${absentScanned} <span style="font-size:14px">(${absentScannedPct}%)</span></div></div>
<div class="abs-box general"><div><div style="font-weight:bold;color:#475569">Absents Generaux (pas scannes)</div><div style="font-size:11px;color:#64748b">N'ont pas scanne du tout</div></div><div style="font-size:28px;font-weight:bold;color:#475569">${absentGeneral} <span style="font-size:14px">(${absentGeneralPct}%)</span></div></div>
</div>
<div class="bars">
<div class="bar-row"><span class="bar-lbl">A l'heure</span><div class="bar-bg"><div class="bar-fill" style="width:${onTimePct}%;background:#f97316">${onTimePct}%</div></div></div>
<div class="bar-row"><span class="bar-lbl">En retard</span><div class="bar-bg"><div class="bar-fill" style="width:${latePct}%;background:#f59e0b">${latePct}%</div></div></div>
<div class="bar-row"><span class="bar-lbl">Abs. Scannes</span><div class="bar-bg"><div class="bar-fill" style="width:${absentScannedPct}%;background:#ef4444">${absentScannedPct}%</div></div></div>
<div class="bar-row"><span class="bar-lbl">Abs. Generaux</span><div class="bar-bg"><div class="bar-fill" style="width:${absentGeneralPct}%;background:#94a3b8">${absentGeneralPct}%</div></div></div>
</div>
<table><thead><tr><th>Date</th><th>Heure</th><th>Matricule</th><th>Nom</th><th>Departement</th><th>Statut</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">ScannerBenj Pro - Rapport genere automatiquement</div>
</body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
    toast.success("PDF en cours de generation...");
  };

  const exportToCSV = () => {
    if (filteredAttendance.length === 0) { toast.error("Aucune donnee a exporter"); return; }
    const headers = ["Date", "Heure", "Matricule", "Nom", "Departement", "Statut"];
    const csvContent = [
      headers.join(","),
      ...getRows().map(({record, label}) => [
        record.date,
        record.time.substring(0, 5),
        record.matricule,
        record.worker_name,
        record.department_name || "N/A",
        label
      ].join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `rapport_scanner_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success("CSV exporte avec succes");
  };

  const exportToExcel = () => {
    if (filteredAttendance.length === 0) { toast.error("Aucune donnee a exporter"); return; }
    const rows = getRows().map(({record, label, color}) =>
      `<tr><td>${new Date(record.date).toLocaleDateString("fr-FR")}</td><td>${record.time.substring(0,5)}</td><td>${record.matricule}</td><td>${record.worker_name}</td><td>${record.department_name || "N/A"}</td><td style="color:${color};font-weight:bold">${label}</td></tr>`
    ).join("");
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"/><style>
th{background:#f97316;color:white;font-weight:bold;padding:8px;border:1px solid #ccc}
td{padding:7px;border:1px solid #ddd}
tr:nth-child(even){background:#fff7ed}
.title{font-size:16px;font-weight:bold;color:#f97316;margin-bottom:10px}
.stats-row td{background:#f8fafc;font-weight:bold;text-align:center}
</style></head><body>
<p class="title">ScannerBenj Pro - Rapport de Pointage - ${new Date().toLocaleDateString("fr-FR")}</p>
<table><tr><td style="background:#fff7ed;color:#f97316;font-weight:bold">A l'heure</td><td style="background:#fef3c7;color:#f59e0b;font-weight:bold">En retard</td><td style="background:#fee2e2;color:#ef4444;font-weight:bold">Absent</td></tr>
<tr class="stats-row"><td>${onTimeCount} (${onTimePct}%)</td><td>${lateCount} (${latePct}%)</td><td>${absentCount} (${absentPct}%)</td></tr></table>
<br/>
<table><thead><tr><th>Date</th><th>Heure</th><th>Matricule</th><th>Nom</th><th>Departement</th><th>Statut</th></tr></thead>
<tbody>${rows}</tbody></table>
</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `rapport_scanner_${new Date().toISOString().split("T")[0]}.xls`;
    link.click();
    toast.success("Excel exporte avec succes");
  };

  const exportToPPT = () => {
    if (filteredAttendance.length === 0) { toast.error("Aucune donnee a exporter"); return; }
    const rows = getRows().map(({record, label, color}) =>
      `<tr><td>${new Date(record.date).toLocaleDateString("fr-FR")}</td><td>${record.time.substring(0,5)}</td><td>${record.matricule}</td><td>${record.worker_name}</td><td>${record.department_name || "N/A"}</td><td style="color:${color};font-weight:bold">${label}</td></tr>`
    ).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Rapport Scanner PPT</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,sans-serif;background:#1e293b;color:white;padding:40px}
.slide{background:linear-gradient(135deg,#1e293b,#0f172a);border:2px solid #f97316;border-radius:16px;padding:40px;margin-bottom:40px;page-break-after:always}
.slide h1{font-size:32px;color:#f97316;text-align:center;margin-bottom:10px}
.slide .sub{text-align:center;color:#94a3b8;margin-bottom:30px}
.stats-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-bottom:30px}
.stat-box{border-radius:12px;padding:25px;text-align:center}
.stat-box.g{background:#f9731620;border:2px solid #f97316}
.stat-box.o{background:#f59e0b20;border:2px solid #f59e0b}
.stat-box.r{background:#ef444420;border:2px solid #ef4444}
.stat-box .num{font-size:48px;font-weight:bold}
.stat-box.g .num{color:#f97316}.stat-box.o .num{color:#f59e0b}.stat-box.r .num{color:#ef4444}
.stat-box .pct{font-size:24px;font-weight:bold;margin-top:5px}
.bar-section{margin-bottom:20px}
.bar-row{display:flex;align-items:center;margin-bottom:12px}
.bar-lbl{width:120px;font-size:14px;color:#cbd5e1}
.bar-bg{flex:1;background:#334155;border-radius:8px;height:28px;overflow:hidden}
.bar-fill{height:100%;border-radius:8px;display:flex;align-items:center;padding-left:10px;color:white;font-size:13px;font-weight:bold}
.slide2{background:linear-gradient(135deg,#0f172a,#1e293b)}
table{width:100%;border-collapse:collapse;font-size:13px}
th{background:#f97316;color:white;padding:10px 12px;text-align:left}
td{padding:9px 12px;border-bottom:1px solid #334155;color:#e2e8f0}
tr:nth-child(even) td{background:#0f172a}
</style></head><body>
<div class="slide">
<h1>ScannerBenj Pro</h1>
<div class="sub">Rapport de Pointage — ${new Date().toLocaleDateString("fr-FR")} | ${filteredAttendance.length} enregistrements</div>
<div class="stats-grid">
<div class="stat-box g"><div class="num">${onTimeCount}</div><div>A l'heure</div><div class="pct" style="color:#f97316">${onTimePct}%</div></div>
<div class="stat-box o"><div class="num">${lateCount}</div><div>En retard</div><div class="pct" style="color:#f59e0b">${latePct}%</div></div>
<div class="stat-box r"><div class="num">${absentCount}</div><div>Absent</div><div class="pct" style="color:#ef4444">${absentPct}%</div></div>
</div>
<div class="bar-section">
<div class="bar-row"><span class="bar-lbl">A l'heure</span><div class="bar-bg"><div class="bar-fill" style="width:${onTimePct}%;background:#f97316">${onTimePct}%</div></div></div>
<div class="bar-row"><span class="bar-lbl">En retard</span><div class="bar-bg"><div class="bar-fill" style="width:${latePct}%;background:#f59e0b">${latePct}%</div></div></div>
<div class="bar-row"><span class="bar-lbl">Absent</span><div class="bar-bg"><div class="bar-fill" style="width:${absentPct}%;background:#ef4444">${absentPct}%</div></div></div>
</div>
</div>
<div class="slide slide2">
<h1 style="margin-bottom:20px">Detail des Pointages</h1>
<table><thead><tr><th>Date</th><th>Heure</th><th>Matricule</th><th>Nom</th><th>Departement</th><th>Statut</th></tr></thead>
<tbody>${rows}</tbody></table>
</div>
</body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
    toast.success("PowerPoint en cours de generation...");
  };

  const resetFilters = () => { setDateFilter(""); setWorkerFilter("all"); setDeptFilter("all"); };


  const statusLabel = (s) => s === "on_time" ? "A l'heure" : s === "late" ? "Retard" : s === "absent_general" ? "Absent (non scanne)" : "Absent (scanne tard)";
  const statusColor = (s) => s === "on_time" ? "bg-orange-100 text-orange-800" : s === "late" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-5">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <div className="bg-orange-100 p-2.5 rounded-xl">
            <FileText className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-manrope font-bold text-slate-900 tracking-tight" data-testid="reports-title">Rapports</h1>
            <p className="text-sm text-slate-500">Historique et exports des pointages</p>
          </div>
        </div>
      </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <Card className="border-orange-200 bg-orange-50"><CardContent className="pt-6 text-center"><p className="text-4xl font-bold text-orange-600">{onTimeCount}</p><p className="text-sm text-orange-700 mt-1">A l'heure</p><p className="text-xl font-bold text-orange-600">{onTimePct}%</p></CardContent></Card>
        <Card className="border-amber-200 bg-amber-50"><CardContent className="pt-6 text-center"><p className="text-4xl font-bold text-amber-600">{lateCount}</p><p className="text-sm text-amber-700 mt-1">En retard</p><p className="text-xl font-bold text-amber-600">{latePct}%</p></CardContent></Card>
        <Card className="border-rose-200 bg-rose-50"><CardContent className="pt-6 text-center"><p className="text-4xl font-bold text-rose-600">{absentCount}</p><p className="text-sm text-rose-700 mt-1">Absents Total</p><p className="text-xl font-bold text-rose-600">{absentPct}%</p></CardContent></Card>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="border-l-4 border-l-rose-400 bg-rose-50">
          <CardContent className="pt-5">
            <div className="flex justify-between items-center">
              <div><p className="text-sm font-semibold text-rose-600">Absents Scannes (trop tard)</p><p className="text-3xl font-bold text-rose-700 mt-1">{absentScanned}</p><p className="text-xs text-slate-500 mt-1">Ont scanne apres la limite</p></div>
              <p className="text-2xl font-bold text-rose-500">{absentScannedPct}%</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400 bg-slate-50">
          <CardContent className="pt-5">
            <div className="flex justify-between items-center">
              <div><p className="text-sm font-semibold text-slate-600">Absents Generaux (pas scannes)</p><p className="text-3xl font-bold text-slate-700 mt-1">{absentGeneral}</p><p className="text-xs text-slate-500 mt-1">N'ont pas scanne du tout</p></div>
              <p className="text-2xl font-bold text-slate-500">{absentGeneralPct}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {pieData.length > 0 && (
        <Card className="mb-6">
          <CardHeader><CardTitle>Repartition des Presences</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={380}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={130} dataKey="value" label={({name, percent}) => `${name}: ${(percent*100).toFixed(0)}%`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center"><Filter className="h-5 w-5 mr-2 text-orange-500" />Filtres</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div><Label>Date</Label><Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} /></div>
            <div><Label>Ouvrier</Label>
              <Select value={workerFilter} onValueChange={setWorkerFilter}>
                <SelectTrigger><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous</SelectItem>{workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.matricule})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Departement</Label>
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous</SelectItem>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button onClick={resetFilters} variant="outline" className="w-full">Reinitialiser</Button></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <Button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white"><Download className="h-4 w-4 mr-2" />PDF</Button>
            <Button onClick={exportToPPT} className="bg-orange-500 hover:bg-orange-600 text-white"><Download className="h-4 w-4 mr-2" />PowerPoint</Button>
            <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700 text-white"><Download className="h-4 w-4 mr-2" />CSV</Button>
            <Button onClick={exportToExcel} className="bg-emerald-700 hover:bg-emerald-800 text-white"><Download className="h-4 w-4 mr-2" />Excel</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Historique ({filteredAttendance.length} enregistrements)</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="reports-table">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Heure</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Matricule</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Nom</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Departement</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Statut</th>
              </tr></thead>
              <tbody>
                {filteredAttendance.length === 0 ? <tr><td colSpan="6" className="text-center py-8 text-slate-500">Aucun enregistrement</td></tr>
                : getRows().map(({record, label, color}, i) => {
                  const style = label === "A l'heure" ? "bg-orange-100 text-orange-800" : label === "Retard" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";
                  return <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`report-row-${i}`}>
                    <td className="py-3 px-4 text-sm">{new Date(record.date).toLocaleDateString("fr-FR")}</td>
                    <td className="py-3 px-4 text-sm font-mono">{record.time.substring(0,5)}</td>
                    <td className="py-3 px-4 text-sm font-mono">{record.matricule}</td>
                    <td className="py-3 px-4 text-sm">
                      <button onClick={() => fetchWorkerHistory(record.worker_id, record.worker_name)} className="text-orange-600 hover:text-orange-800 font-semibold hover:underline cursor-pointer">{record.worker_name}</button>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{record.department_name || "N/A"}</td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${style}`}>{label}</span></td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal historique ouvrier */}
      {selectedWorker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-2 rounded-lg"><User className="h-5 w-5 text-orange-600" /></div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedWorker}</h2>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Jours de culte — {cultDays.map(d => ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"][d]).join(", ")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCultEditor(!showCultEditor)} title="Configurer jours de culte"
                  className="p-2 hover:bg-orange-50 rounded-lg text-orange-500 hover:text-orange-700 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                <button onClick={() => { setSelectedWorker(null); setWorkerHistory(null); setShowCultEditor(false); }} className="p-2 hover:bg-slate-100 rounded-lg"><X className="h-5 w-5 text-slate-500" /></button>
              </div>
            </div>

            {/* Editeur jours de culte */}
            {showCultEditor && (
              <div className="px-6 py-4 bg-orange-50 border-b border-orange-100 flex-shrink-0">
                <p className="text-sm font-semibold text-orange-800 mb-3">Jours de culte actifs :</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"].map((name, d) => (
                    <button key={d} onClick={() => toggleCultDay(d)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        cultDays.includes(d)
                          ? "bg-orange-500 text-white border-orange-500 shadow"
                          : "bg-white text-slate-500 border-slate-300 hover:border-orange-300"
                      }`}>
                      {name}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={saveCultDays} disabled={savingCult || cultDays.length === 0}
                    className="px-4 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors">
                    {savingCult ? "Sauvegarde..." : "Sauvegarder"}
                  </button>
                  <button onClick={() => setShowCultEditor(false)}
                    className="px-4 py-1.5 bg-white text-slate-600 text-xs font-semibold rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                    Annuler
                  </button>
                </div>
              </div>
            )}

            <div className="p-6 overflow-y-auto flex-1">
              {loadingHistory ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>
              ) : workerHistory ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-orange-600">{workerHistory.history.filter(h => h.status === "on_time").length}</p>
                      <p className="text-xs text-orange-700 mt-1">A l'heure</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">{workerHistory.history.filter(h => h.status === "late").length}</p>
                      <p className="text-xs text-amber-700 mt-1">En retard</p>
                    </div>
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-rose-600">{workerHistory.history.filter(h => h.status !== "on_time" && h.status !== "late").length}</p>
                      <p className="text-xs text-rose-700 mt-1">Absents</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {workerHistory.history.map((h, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${h.scanned ? "bg-slate-50 border-slate-100" : "bg-rose-50 border-rose-100"}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-slate-600 w-28">{new Date(h.date + "T12:00:00").toLocaleDateString("fr-FR", {weekday:"short", day:"2-digit", month:"2-digit"})}</span>
                          {h.time ? (
                            <span className="text-sm font-mono text-slate-500">{h.time.substring(0,5)}</span>
                          ) : (
                            <span className="text-xs text-rose-400 italic">Non scanné</span>
                          )}
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${statusColor(h.status)}`}>{statusLabel(h.status)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
